import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Rating,
  IconButton,
  Skeleton,
  TextField,
  Alert,
  Collapse,
  Divider,
  Chip,
} from '@mui/material';
import {
  Instagram as InstagramIcon,
  Schedule as ScheduleIcon,
  Star as StarIcon,
  RateReview as RateReviewIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  DirectionsOutlined as DirectionsIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { siteSettingsAPI, reviewsAPI } from '../utils/api';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
};

function LandingSection({ onBookNow }) {
  const [settings, setSettings] = useState(null);
  const [reviews, setReviews] = useState({ reviews: [], stats: {} });
  const [loading, setLoading] = useState(true);

  // Review form state
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    customer_name: '',
    rating: 5,
    review_text: '',
  });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, reviewsRes] = await Promise.all([
        siteSettingsAPI.getAll(),
        reviewsAPI.getAll(6),
      ]);
      setSettings(settingsRes.data);
      setReviews(reviewsRes.data);
    } catch (error) {
      console.error('Error fetching landing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!reviewForm.customer_name.trim()) {
      toast.error('Please enter your name');
      return;
    }

    setSubmittingReview(true);
    try {
      await reviewsAPI.submit(reviewForm);
      toast.success('Thank you for your review!');
      setReviewSubmitted(true);
      setReviewForm({ customer_name: '', rating: 5, review_text: '' });
      const reviewsRes = await reviewsAPI.getAll(6);
      setReviews(reviewsRes.data);
    } catch (error) {
      toast.error('Failed to submit review. Please try again.');
    } finally {
      setSubmittingReview(false);
    }
  };

  // Load LightWidget script for Instagram embed
  const scriptLoadedRef = useRef(false);
  useEffect(() => {
    if (settings?.instagram_embed && !scriptLoadedRef.current) {
      const existingScript = document.querySelector('script[src*="lightwidget.com"]');
      if (!existingScript) {
        const script = document.createElement('script');
        script.src = 'https://cdn.lightwidget.com/widgets/lightwidget.js';
        script.async = true;
        document.body.appendChild(script);
      }
      scriptLoadedRef.current = true;
    }
  }, [settings?.instagram_embed]);

  // Generate Google Maps URL for directions
  const getDirectionsUrl = () => {
    if (!settings?.address) return '#';
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(settings.address)}`;
  };

  // Generate Google Maps embed URL
  const getMapEmbedUrl = () => {
    if (!settings?.address) return '';
    return `https://www.google.com/maps?q=${encodeURIComponent(settings.address)}&output=embed`;
  };

  if (loading) {
    return (
      <Box sx={{ mb: 4 }}>
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2, mb: 2 }} />
        <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  const averageRating = parseFloat(reviews.stats.averageRating) || 0;
  const totalReviews = parseInt(reviews.stats.totalReviews) || 0;

  return (
    <Box sx={{ mb: 4 }}>
      {/* ===== HEADER: Salon Name, Rating, Address ===== */}
      <Card sx={{ mb: 3, overflow: 'visible' }}>
        <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
          {/* Salon Name */}
          <Typography
            variant="h4"
            fontWeight={700}
            sx={{
              color: 'primary.main',
              mb: 1,
              fontSize: { xs: '1.75rem', md: '2.25rem' },
            }}
          >
            {settings?.about_title || 'Studio Jen'}
          </Typography>

          {/* Rating */}
          {totalReviews > 0 && (
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Rating value={averageRating} precision={0.1} readOnly size="small" />
              <Typography variant="body2" fontWeight={600}>
                {averageRating.toFixed(1)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ({totalReviews} reviews)
              </Typography>
            </Box>
          )}

          {/* Address */}
          {settings?.address && (
            <Box display="flex" alignItems="flex-start" gap={1} mb={1}>
              <LocationIcon sx={{ color: 'text.secondary', fontSize: 20, mt: 0.25 }} />
              <Typography variant="body2" color="text.secondary">
                {settings.address}
              </Typography>
            </Box>
          )}

          {/* Contact Row */}
          <Box display="flex" flexWrap="wrap" gap={2} mt={2}>
            {settings?.phone_number && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <PhoneIcon sx={{ color: 'primary.main', fontSize: 18 }} />
                <Typography
                  variant="body2"
                  component="a"
                  href={`tel:${settings.phone_number}`}
                  sx={{ color: 'primary.main', textDecoration: 'none', fontWeight: 500 }}
                >
                  {settings.phone_number}
                </Typography>
              </Box>
            )}
            {settings?.email_address && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <EmailIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                <Typography
                  variant="body2"
                  component="a"
                  href={`mailto:${settings.email_address}`}
                  sx={{ color: 'text.secondary', textDecoration: 'none' }}
                >
                  {settings.email_address}
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* ===== PROMINENT BOOK NOW BUTTON ===== */}
      <Button
        variant="contained"
        size="large"
        fullWidth
        onClick={onBookNow}
        sx={{
          mb: 3,
          py: 1.75,
          fontSize: '1.1rem',
          fontWeight: 600,
          borderRadius: 2,
          boxShadow: '0 4px 14px rgba(139, 38, 53, 0.3)',
          '&:hover': {
            boxShadow: '0 6px 20px rgba(139, 38, 53, 0.4)',
          },
        }}
      >
        Book Now
      </Button>

      {/* ===== REVIEWS SECTION ===== */}
      {reviews.reviews.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <StarIcon sx={{ color: '#FFD700' }} />
              <Typography variant="h6" fontWeight={600}>Reviews</Typography>
            </Box>

            {/* Show last few reviews */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {reviews.reviews.slice(0, 3).map((review, idx) => (
                <Box
                  key={review.id || idx}
                  sx={{
                    p: 2,
                    bgcolor: '#FAF8F5',
                    borderRadius: 2,
                    borderLeft: '3px solid #D4A574',
                  }}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2" fontWeight={600}>
                      {review.customer_name}
                    </Typography>
                    <Rating value={review.rating} readOnly size="small" />
                  </Box>
                  {review.review_text && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontStyle: 'italic', fontSize: '0.875rem' }}
                    >
                      "{review.review_text}"
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>

            {/* Leave a Review - Collapsible */}
            <Box sx={{ mt: 3 }}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                sx={{ cursor: 'pointer' }}
                onClick={() => setShowReviewForm(!showReviewForm)}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <RateReviewIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                  <Typography variant="body2" fontWeight={500} color="primary.main">
                    Leave a Review
                  </Typography>
                </Box>
                <IconButton size="small">
                  {showReviewForm ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>

              <Collapse in={showReviewForm}>
                <Box sx={{ mt: 2 }}>
                  {reviewSubmitted ? (
                    <Alert severity="success">
                      Thank you for your review!
                    </Alert>
                  ) : (
                    <form onSubmit={handleSubmitReview}>
                      <TextField
                        fullWidth
                        label="Your Name"
                        value={reviewForm.customer_name}
                        onChange={(e) => setReviewForm({ ...reviewForm, customer_name: e.target.value })}
                        required
                        sx={{ mb: 2 }}
                        size="small"
                      />
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Your Rating
                        </Typography>
                        <Rating
                          value={reviewForm.rating}
                          onChange={(e, newValue) => setReviewForm({ ...reviewForm, rating: newValue || 5 })}
                          size="large"
                        />
                      </Box>
                      <TextField
                        fullWidth
                        label="Your Review (optional)"
                        value={reviewForm.review_text}
                        onChange={(e) => setReviewForm({ ...reviewForm, review_text: e.target.value })}
                        multiline
                        rows={3}
                        sx={{ mb: 2 }}
                        size="small"
                      />
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={submittingReview}
                        size="small"
                      >
                        {submittingReview ? 'Submitting...' : 'Submit Review'}
                      </Button>
                    </form>
                  )}
                </Box>
              </Collapse>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* ===== ABOUT SECTION ===== */}
      {settings?.about_text && (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Box display="flex" alignItems="center" gap={1} mb={1.5}>
              <InfoIcon sx={{ color: 'primary.main', fontSize: 20 }} />
              <Typography variant="h6" fontWeight={600}>About</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
              {settings.about_text}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* ===== OPENING TIMES ===== */}
      {settings?.business_hours && (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <ScheduleIcon sx={{ color: 'primary.main', fontSize: 20 }} />
              <Typography variant="h6" fontWeight={600}>Opening Times</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {DAYS.map((day) => {
                const hours = settings.business_hours[day];
                const isToday = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase() === day;
                return (
                  <Box
                    key={day}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 0.75,
                      px: 1.5,
                      borderRadius: 1,
                      bgcolor: isToday ? 'rgba(139, 38, 53, 0.08)' : 'transparent',
                      borderLeft: isToday ? '3px solid' : '3px solid transparent',
                      borderColor: isToday ? 'primary.main' : 'transparent',
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={isToday ? 600 : 400}
                      color={isToday ? 'primary.main' : 'text.primary'}
                    >
                      {DAY_LABELS[day]}
                      {isToday && (
                        <Chip
                          label="Today"
                          size="small"
                          sx={{
                            ml: 1,
                            height: 20,
                            fontSize: '0.65rem',
                            bgcolor: 'primary.main',
                            color: 'white',
                          }}
                        />
                      )}
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={isToday ? 600 : 400}
                      color={hours?.closed ? 'text.disabled' : (isToday ? 'primary.main' : 'text.primary')}
                    >
                      {hours?.closed ? 'Closed' : `${hours?.open} - ${hours?.close}`}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* ===== INSTAGRAM FEED ===== */}
      {settings?.instagram_embed && (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <InstagramIcon sx={{ color: '#E4405F' }} />
              <Typography variant="h6" fontWeight={600}>Follow Us</Typography>
            </Box>
            <Box
              sx={{
                '& iframe': {
                  width: '100%',
                  minHeight: 350,
                  border: 0,
                },
              }}
              dangerouslySetInnerHTML={{
                __html: settings.instagram_embed.replace(/<script[^>]*>.*?<\/script>/gi, ''),
              }}
            />
          </CardContent>
        </Card>
      )}

      {/* ===== MAP & DIRECTIONS ===== */}
      {settings?.address && (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <LocationIcon sx={{ color: 'primary.main', fontSize: 20 }} />
              <Typography variant="h6" fontWeight={600}>Location</Typography>
            </Box>

            {/* Map Embed */}
            <Box
              sx={{
                width: '100%',
                height: 250,
                borderRadius: 2,
                overflow: 'hidden',
                mb: 2,
                bgcolor: '#f5f5f5',
              }}
            >
              <iframe
                src={getMapEmbedUrl()}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Location Map"
              />
            </Box>

            {/* Address & Directions Button */}
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
              <Typography variant="body2" color="text.secondary">
                {settings.address}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DirectionsIcon />}
                href={getDirectionsUrl()}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ flexShrink: 0 }}
              >
                Get Directions
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* ===== ADDITIONAL INFO / POLICIES ===== */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Additional Information
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              • Card details required to secure bookings (not charged)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Free cancellation with 48+ hours notice
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Late arrivals may have appointments shortened
            </Typography>
          </Box>
          <Box sx={{ mt: 2 }}>
            <Link
              to="/policies"
              style={{
                color: '#8B2635',
                textDecoration: 'none',
                fontWeight: 500,
                fontSize: '0.875rem',
              }}
            >
              View full policies →
            </Link>
          </Box>
        </CardContent>
      </Card>

      {/* ===== BOTTOM BOOK NOW BUTTON ===== */}
      <Button
        variant="contained"
        size="large"
        fullWidth
        onClick={onBookNow}
        sx={{
          py: 1.75,
          fontSize: '1.1rem',
          fontWeight: 600,
          borderRadius: 2,
          boxShadow: '0 4px 14px rgba(139, 38, 53, 0.3)',
          '&:hover': {
            boxShadow: '0 6px 20px rgba(139, 38, 53, 0.4)',
          },
        }}
      >
        Book Your Appointment
      </Button>
    </Box>
  );
}

export default LandingSection;
